import React, { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import {
  CourseImageUpload,
  createCourse,
  getCourseByID,
  updateCourse,
} from "./services/course.services";
import { clearCourseDetails } from "./courseSlice";

import FormRichTextField from "../../components/formFields/FormRichTextField";
// import FormImageUpload from "../../components/formFields/FormImageUpload";
import FormImageUploadMorden from "../../components/formFields/formImageUploadMorden";

const CreateCourse: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const CourseID = location.state?.CourseID;
  const CourseDetail = useAppSelector((state) => state.course.CourseDetail);
  const loading = useAppSelector((state) => state.course.loading);

  useEffect(() => {
    if (CourseID) {
      dispatch(getCourseByID(CourseID));
    }
    return () => {
      dispatch(clearCourseDetails());
    };
  }, [CourseID]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ? values.Title : "",
      Description: values?.Description ? values?.Description : "",
      URL: values?.URL ? values?.URL : "",
      IsActive: values.IsActive === 1,
    };

    // console.log("values?.Logo", values?.Logo);
    const ImageData = new FormData();
    const Image =
      values?.Logo &&
      typeof values?.Logo !== "string" &&
      Array.isArray(values?.Logo) &&
      values?.Logo?.length > 0
        ? values.Logo[0].getRawFile()
        : "";

    try {
      if (CourseID) {
        const editPayload = {
          ...formData,
          ID: CourseID,
        };
        const response = await dispatch(updateCourse(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Logo &&
            typeof values?.Logo !== "string" &&
            Array.isArray(values?.Logo) &&
            values?.Logo?.length > 0
          ) {
            ImageData.append("Logo", Image);
            ImageData.append("ID", CourseID);
            const imageResponse = await dispatch(CourseImageUpload(ImageData));
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/course");
            } else {
              navigate("/course");
            }
          } else {
            navigate("/course");
          }
        }
      } else {
        const response = await dispatch(createCourse(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            values?.Logo &&
            typeof values?.Logo !== "string" &&
            Array.isArray(values?.Logo) &&
            values?.Logo?.length > 0 &&
            response?.payload?.Data?.ID
          ) {
            ImageData.append("Logo", Image);
            ImageData.append("ID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(CourseImageUpload(ImageData));
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/course");
            } else {
              navigate("/course");
            }
          } else {
            navigate("/course");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      <div>
        <Form
          onSubmit={handleSubmit}
          initialValues={CourseDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 40 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4>
                      {CourseID ? "Update Course" : "Create Course"}
                    </Typography.h4>
                  </GridLayoutItem>

                  <GridLayoutItem colSpan={2} rowSpan={2}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      name="URL"
                      label="Course (URL)"
                      placeholder="i.e /coaching/sat"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      justifyContent="start"
                      name={`Logo`}
                      label="Logo"
                      type="file"
                      accept="image/*"
                      style={{ width: 100 }}
                      alternateName="Logo"
                      component={FormImageUploadMorden}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2} rowSpan={2}>
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      // validator={requiredValidator}
                      // astrike={true}
                      style={{ height: 250 }}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={CourseID ? "Update" : "Create"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/course")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateCourse;
